import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CompetencyCard from "@/pages/CompetencyCard";
import MyTests from "@/pages/MyTests";
import TakeTest from "@/pages/TakeTest";
import TNIMatrix from "@/pages/TNIMatrix";
import SkillMatrix from "@/pages/SkillMatrix";
import TrainingCalendar from "@/pages/TrainingCalendar";
import CalendarEntryDetail from "@/pages/CalendarEntryDetail";
import TrainingsList from "@/pages/TrainingsList";
import Employees from "@/pages/Employees";
import AssignTest from "@/pages/AssignTest";
import SystemConfig from "@/pages/SystemConfig";
import DataImport from "@/pages/DataImport";
import DataAudit from "@/pages/DataAudit";
import CompetencyMapEditor from "@/pages/CompetencyMapEditor";

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Take test uses full-screen layout (no shell) */}
          <Route
            path="/test/:assessmentId"
            element={
              <ProtectedRoute>
                <TakeTest />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Shell><Dashboard /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-card"
            element={
              <ProtectedRoute>
                <Shell><CompetencyCard /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/competency-card/:employeeId"
            element={
              <ProtectedRoute>
                <Shell><CompetencyCard /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-tests"
            element={
              <ProtectedRoute>
                <Shell><MyTests /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tni-matrix"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><TNIMatrix /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tni-matrix/staff"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><TNIMatrix workforceType="staff" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tni-matrix/operators"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><TNIMatrix workforceType="worker" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/skill-matrix"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><SkillMatrix /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/skill-matrix/staff"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><SkillMatrix workforceType="staff" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/skill-matrix/operators"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><SkillMatrix workforceType="worker" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><TrainingCalendar /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar/:calendarId"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><CalendarEntryDetail /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainings"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><TrainingsList /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><Employees /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/staff"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><Employees workforceType="staff" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/operators"
            element={
              <ProtectedRoute roles={["hr", "quality", "management", "super_admin"]}>
                <Shell><Employees workforceType="worker" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assign-test"
            element={
              <ProtectedRoute roles={["hr", "super_admin"]}>
                <Shell><AssignTest /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assign-test/staff"
            element={
              <ProtectedRoute roles={["hr", "super_admin"]}>
                <Shell><AssignTest workforceType="staff" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assign-test/operators"
            element={
              <ProtectedRoute roles={["hr", "super_admin"]}>
                <Shell><AssignTest workforceType="worker" /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/competency-map"
            element={
              <ProtectedRoute roles={["hr", "super_admin"]}>
                <Shell><CompetencyMapEditor /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/config"
            element={
              <ProtectedRoute roles={["super_admin"]}>
                <Shell><SystemConfig /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-import"
            element={
              <ProtectedRoute roles={["super_admin"]}>
                <Shell><DataImport /></Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-audit"
            element={
              <ProtectedRoute roles={["super_admin"]}>
                <Shell><DataAudit /></Shell>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
